import * as React from 'react'

import { Box, Button, type SelectOption, Text } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Select from 'modules/core/components/Inputs/Select'
import InputValue from 'modules/core/components/MultiFilters/FilterItem/InputValue'
import useFilterItem from 'modules/core/components/MultiFilters/FilterItem/useFilterItem'
import type LogicOperatorForm from 'modules/core/components/MultiFilters/Forms/LogicOperatorForm'
import type MultiFilterForm from 'modules/core/components/MultiFilters/Forms/MultiFilterForm'
import type { FilterOption } from 'modules/core/components/MultiFilters/types'
import i18n from 'modules/core/lib/i18n'

interface Props {
  index?: number
  fields: readonly FilterOption[]
  filter: MultiFilterForm
  logicOperator: LogicOperatorForm
  onRemoveFilter: (index: number) => void
}

const FilterRow = ({ index = 0, fields, filter, logicOperator, onRemoveFilter }: Props) => {
  const { useSelectedField, useNeedValue, useConditions, useLogicOperatorLabel, logicOptions } =
    useFilterItem()
  const value = filter.get('value')
  const customAction = filter.get('custom_action')
  const field = filter.get('field')
  const fieldValue = filter.get('field').value
  const condition = filter.get('condition')
  const conditionValue = filter.get('condition').value
  const logicOperatorValue = logicOperator.get('logic_operator').value
  const fieldOptions: FilterOption | undefined = useSelectedField(fields, fieldValue).get()
  const needValue = useNeedValue(conditionValue).get() && fieldOptions !== undefined
  const conditions: SelectOption[] = useConditions(fieldOptions, customAction.value).get()
  const logicOperatorLabel = useLogicOperatorLabel(logicOperatorValue, logicOptions).get()
  React.useEffect(() => {
    if (!fieldOptions) return

    customAction.set(fieldOptions.customAction)
  }, [customAction, fieldOptions])

  const onFieldChange = () => {
    value.set('')
  }

  return (
    <Box gap="s8" flexDirection="row" alignItems="center">
      <Box width="s80" flexDirection="row" justifyContent="flexEnd">
        {index === 1 ? (
          <Select
            hideLabel
            field={logicOperator.get('logic_operator')}
            options={logicOptions}
            none={i18n.t('filters.filter.none')}
          />
        ) : (
          <Box width="s80" flexDirection="row" justifyContent="flexEnd">
            <Text.Normal weight="medium">
              {index === 0
                ? i18n.t('filters.multi_filters.logic_operators.where')
                : logicOperatorLabel}
            </Text.Normal>
          </Box>
        )}
      </Box>
      <Box width="full">
        <Select
          hideLabel
          field={field}
          options={fields.map((fieldOption) => ({
            label: fieldOption.label,
            value: fieldOption.value,
          }))}
          onChange={onFieldChange}
          none={i18n.t('filters.filter.none')}
        />
      </Box>
      <Box width={needValue ? 's120' : 'full'}>
        <Select
          hideLabel
          field={condition}
          options={conditions}
          none={i18n.t('filters.filter.none')}
        />
      </Box>
      {needValue && (
        <Box gap="s8" width="full" flexDirection="row" alignItems="center">
          <InputValue field={value} options={fieldOptions} condition={conditionValue} />
        </Box>
      )}
      <Button onClick={() => onRemoveFilter(index)} type="secondary" iconName="delete" />
    </Box>
  )
}

export default observer(FilterRow)
